import React, { useRef, useState, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { ArrowDropDown } from '@mui/icons-material';
import Logo from '@images/logo-initial.svg';
import { useAppContext } from '../../../contexts/AppContextProvider';
import useOnClickOutside from '@hooks/useOnClickOutside';
import { logout } from '../../../API/Auth';
import './style.css';

interface HeaderProps {
  handleMenuToggle: () => void;
}

const Header: React.FC<HeaderProps> = () => {
  const { value } = useAppContext();
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const userName = value?.user?.name || 'User';

  const handleClickOutside = useCallback((): void => {
    setIsDropdownOpen(false);
  }, []);

  useOnClickOutside(dropdownRef, handleClickOutside);

  const handleLogout = useCallback((): void => {
    logout();
  }, []);

  const toggleDropdown = useCallback((): void => {
    setIsDropdownOpen((prevState) => !prevState);
  }, []);

  return (
    <div className="main-header-div sticky top-0 z-50 shadow-sm border-b border-gray-100">
      <div className="main-header-flex flex items-center justify-between px-4 py-3 md:px-8 lg:px-12">
        <div className="logo-main-div flex items-center">
          <NavLink to="/app/users" className="flex items-center gap-3 transition-transform hover:scale-105">
            <img 
              src={Logo} 
              alt="Company Logo" 
              className="logo-icon h-9 md:h-12 w-auto object-contain"
            />
            <div className="flex flex-col text-left justify-center">
              <span className="font-bold text-lg md:text-xl leading-none text-[#051042] tracking-wide">
                CALLIGENT
              </span>
              <span className="text-[9px] md:text-[10px] font-bold text-[#8c94a5] tracking-[0.18em] uppercase leading-none mt-1">
                AI EMPLOYEES
              </span>
            </div>
          </NavLink>
        </div>

        <div className="header-profile flex items-center space-x-3 md:space-x-6">
          <div className="dropdown relative" ref={dropdownRef}>
            <button
              className="dropdown-button flex items-center space-x-2 md:space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-all duration-200"
              onClick={toggleDropdown}
              aria-expanded={isDropdownOpen}
              aria-haspopup="true"
            >
              <div className="inner-wrapper-profile flex items-center space-x-2 md:space-x-3">
                <div className="profile-content hidden md:block text-left">
                  <h4 className="text-sm font-semibold text-gray-900 capitalize">
                    Hi, {userName}
                  </h4>
                  <p className="text-xs text-gray-500">Welcome Back</p>
                </div>
                <div className="dropdown-img">
                  <ArrowDropDown className={`text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </button>
            
{isDropdownOpen && (
              <div 
                className="dropdown-content show absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50"
                style={{ display: 'block' }}
                role="menu"
              >
                <NavLink
                  to="/app/profile"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  role="menuitem"
                  onClick={toggleDropdown}
                >
                  <svg 
                    className="w-4 h-4 mr-3" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
                    />
                  </svg>
                  My Profile
                </NavLink>


                <NavLink
                  to="/auth/login"
                  replace={true}
                  onClick={handleLogout}
                  className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  role="menuitem"
                >
                  <svg 
                    className="w-4 h-4 mr-3" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
                    />
                  </svg>
                  Log Out
                </NavLink>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;